import { useQuery } from '@tanstack/react-query'
import { deviceFloorPositionQuery, type FloorPosition } from '../api/floorPlans'
import { cmToPx } from './geometry'
import './FloorPlan.css'

/**
 * "Where is this device" - pe ecranul Connection Quality al aparatului (fila
 * fisei si pagina mare), cand aparatul e pus pe un plan de etaj (faza 5).
 * Fara OpenLayers: planul ca imagine, un punct pentru aparat asezat in
 * procente si puncte mici, cu nume, pentru AP-urile puse pe acelasi etaj -
 * cel la care e conectat acum (`currentPrefixes`, BssidGroups::prefix) iese in
 * evidenta. Nimic cand aparatul nu e pe niciun plan. Un etaj desenat fara
 * plan (`canvas`) se arata ca foaie alba cu puncte (la 5 m), de marimea lui.
 */
/** Asezarile care se pot desena (etaj cu scara si cu plan ori foaie). */
function drawable(placements: FloorPosition['placements'] | undefined): FloorPosition['placements'] {
  return (placements ?? []).filter((p) => p.floor.scale_px_per_m && (p.floor.image_url || p.floor.canvas) && p.floor.image_w_px && p.floor.image_h_px)
}

/** Are aparatul ce desena? (Vederea Grid a paginii mari pune cardul numai atunci.) */
export function useHasFloorPosition(id: number): boolean {
  const { data } = useQuery(deviceFloorPositionQuery(id))
  return drawable(data?.placements).length > 0
}

export function DeviceFloorPosition({ id, currentPrefixes = [] }: { id: number; currentPrefixes?: string[] }) {
  const { data } = useQuery(deviceFloorPositionQuery(id))
  const placements = drawable(data?.placements)
  if (placements.length === 0) return null
  return (
    <section className="fp-where" data-section="where-is-this-device">
      <h5>Where is this device</h5>
      {placements.map((p) => {
        const scale = p.floor.scale_px_per_m as number
        const w = p.floor.image_w_px as number
        const h = p.floor.image_h_px as number
        const at = (x: number, y: number) => ({
          left: `${(cmToPx(x, scale) / (p.floor.image_w_px as number)) * 100}%`,
          top: `${(cmToPx(y, scale) / (p.floor.image_h_px as number)) * 100}%`,
        })
        return (
          <div key={p.floor.id} className="fp-where-one">
            <p className="fp-muted">
              {p.floor.name}
              {p.office ? `, ${p.office}` : ''}
              {data?.canOpen ? (
                <>
                  {' '}
                  <a href={`/app/settings/floor_plans/${p.floor.id}`}>Open the floor plan</a>
                </>
              ) : null}
            </p>
            <div className="fp-where-plan">
              {p.floor.image_url ? (
                <img src={p.floor.image_url} alt={`Plan of ${p.floor.name}`} loading="lazy" />
              ) : (
                <div
                  className="fp-where-sheet"
                  role="img"
                  aria-label={`Drawing of ${p.floor.name}`}
                  style={{ aspectRatio: `${w} / ${h}`, width: `min(100%, ${Math.round((320 * w) / h)}px)`, backgroundSize: `${((5 * scale) / w) * 100}% ${((5 * scale) / h) * 100}%` }}
                ></div>
              )}
              {(p.aps ?? []).map((ap) => {
                const current = currentPrefixes.includes(ap.prefix)
                return (
                  <span
                    key={`${ap.key}-${ap.x_cm}-${ap.y_cm}`}
                    className={`fp-where-ap${current ? ' is-current' : ''}`}
                    style={at(ap.x_cm, ap.y_cm)}
                    data-ap={ap.prefix}
                    title={current ? `${ap.name} (connected now)` : ap.name}
                  >
                    <span className="fp-where-ap-name">{ap.name}</span>
                  </span>
                )
              })}
              <span className="fp-where-dot" style={at(p.x_cm, p.y_cm)} aria-label="This device"></span>
            </div>
          </div>
        )
      })}
    </section>
  )
}

/**
 * Butonul "Floor plans" din capul ecranului Connection Quality: la etajul
 * aparatului cand e pus pe unul ("Floor plan: <etaj>"), altfel la lista
 * etajelor. Numai cu dreptul de citire al setarilor firmei (`canOpen`).
 */
export function FloorPlansButton({ id }: { id: number }) {
  const { data } = useQuery(deviceFloorPositionQuery(id))
  if (!data?.canOpen) return null
  const floor = data.floors?.[0] ?? data.placements?.[0]?.floor ?? null
  return (
    <a
      className="btn btn-sm btn-default cq-floor-plans"
      href={floor ? `/app/settings/floor_plans/${floor.id}` : '/app/settings/floor_plans'}
      target="_blank"
      rel="noopener"
      title={floor ? 'The floor plan this device is placed on' : 'The floor plans of the company'}
    >
      <i className="fas fa-map" aria-hidden="true"></i> {floor ? `Floor plan: ${floor.name}` : 'Floor plans'}
    </a>
  )
}
